import { useMemo, useState } from "react";
import { Button, Divider, Typography, Checkbox, Stack, Drawer, IconButton, LinearProgress, Link } from "@mui/material";
import { grey } from "@mui/material/colors";
import { RootState } from "@/store/store";
import { addTips, removeTips } from "./slice";
import { useAppDispatch, useAppSelector, useResponsive, useTerminology } from "@/hooks";
import OrderItemsList from "./components/OrderItemsList";
import { EmptyState } from "@/components";
import Price from "@/components/Price";
import { OrderItem } from "@/types";
import useRoutes from "@/hooks/useRoutes";
import { getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import DineModeTabs from "@/components/dine-mode-tabs/DineModeTabs";
import { useParams } from "react-router-dom";
import { addCurrency } from "@/utils/numbers";
import { Add01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import useServiceFee from "@/hooks/useServiceFee";
import { openBranchDrawer, openLocationDialog, setIsDineModeOpen } from "@/store/uiSlice";
import { MENU_FLOWS, DINING_MODES } from "@/constants/global";
import get from "lodash/get";
import camelCase from "lodash/camelcase";
import { checkServiceAvailability } from "@/utils/weekDays";
import MissingDeliveryLocationBanner from "./components/MissingDeliveryLocationBanner";
import MissingDineModeBanner from "./components/MissingDineModeBanner";
import useGetDistance from "@/hooks/useGetDistance";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { LocalStorage } from "@/constants/localStorage";
import React from "react";
import { v4 } from "uuid";
import useSnackbar from "@/hooks/useSnackbar";

interface Props {
  onClose?: () => void;
}

const Order = ({ onClose }: Props) => {
  const [navigate] = useRoutes();
  const dispatch = useAppDispatch();
  const { isMobile } = useResponsive();
  const { organizationAlias } = useParams<{ organizationAlias: string }>();
  const order = useAppSelector((state: RootState) => state.order);
  const distance = useGetDistance();
  const [openSnackbar] = useSnackbar();
  const { t } = useTerminology();

  const flowParam = new URLSearchParams(document.location.search).get("flow");
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const organization = useAppSelector((state) => state.organizations[organizationId]);
  const [isMinValueDrawerOpen, setIsMinValueDrawerOpen] = useState(false);
  const service = useServiceFee();

  const organizationBranch = useFetchBranchQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });
  const branches = organizationBranch?.data && Array.isArray(organizationBranch.data) ? organizationBranch.data : [];
  const hasBranches = branches.length > 0;
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);

  const toggleTips = () => {
    dispatch(order.areTipsIncluded ? removeTips() : addTips());
  };

  const showTaxes = useMemo(() => {
    return Boolean(organization?.settings?.includeTax);
  }, [organization?.settings?.includeTax]);

  const tips = useMemo(() => {
    if (flowParam === "dine-in") {
      return order?.areTipsIncluded ? getOrderSubtotal(order.items as OrderItem[]) * (order?.tipPercentage / 100) : 0;
    }
    return order?.areTipsIncluded ? 0 : 0;
  }, [flowParam, order?.areTipsIncluded, order.items, order?.tipPercentage]);

  // const areAllItemsDelivered = useMemo(() => {
  //   return !(order?.items ?? []).find((item: OrderItem) => item?.status !== ItemStatus.DELIVERED);
  // }, [order?.items]);

  const dineModes = useMemo(() => {
    const lsBranchId = localStorage.getItem(LocalStorage.BRANCH_ID);
    const branchToUse = branches.find((b) => b.id === activeBranch) 
                     || branches.find((b) => b.id === lsBranchId);

    return DINING_MODES.filter((mode) => {
      if (hasBranches && branchToUse) {
        const hasService = branchToUse.services?.includes(mode.id);
        const isBranchOpen = checkServiceAvailability({
          isEnabled: true,
          schedule: branchToUse.schedule,
        });
        return hasService && isBranchOpen;
      }

      const serviceConfig = get(organization, `settings.services.${camelCase(mode.id)}`);
      return checkServiceAvailability(serviceConfig);
    });
  }, [organization, hasBranches, activeBranch, branches]);

  const banners = useMemo(() => {
    const banners = [];

    //Si no tiene branches la organización debe mostrar el dialogo para seleccionar un flujo de orden 

    if (!order.flow && dineModes.length > 0) {
      banners.push(<MissingDineModeBanner />);
    }

    if (order.flow === "delivery" && (!order.deliveryLocation?.latitude || !order.deliveryLocation?.longitude)) {
      banners.push(<MissingDeliveryLocationBanner key="missing-delivery-location" />);
    }
    if (banners?.length === 0) {
      return null;
    }
    return banners;
  }, [order.deliveryLocation?.latitude, order.deliveryLocation?.longitude, dineModes.length, order.flow]);

  const handleContinue = (orderId: string) => {
    const dineMode = order?.flow;
    const subtotal = getOrderSubtotal(order.items);
    const deliveryMinValue = organization?.settings?.services?.delivery?.fee?.minValue || 0;

    const isDeliveryOrPickupEnabled = organization?.settings?.services?.delivery?.isEnabled || organization?.settings?.services?.pickup?.isEnabled;
    const isDeliveryOrPickupAvailable = dineModes.map(({ id }) => id).includes(MENU_FLOWS.DELIVERY) || dineModes.map(({ id }) => id).includes(MENU_FLOWS.PICK_UP);
    const isDeliveryOrPickupSelected = dineMode === MENU_FLOWS.DELIVERY || dineMode === MENU_FLOWS.PICK_UP;

    //
    const maxDistanceKm = organization?.settings?.services?.delivery?.maxDistanceKm;
    const distanceKm = order?.deliveryLocation?.distance?.value
      ? order.deliveryLocation.distance.value / 1000
      : null;

    if (
      dineMode === MENU_FLOWS.DELIVERY &&
      maxDistanceKm &&
      distanceKm &&
      distanceKm > maxDistanceKm
    ) {
      openSnackbar(
        `La cobertura de entrega no está disponible para tu zona. El rango máximo es de ${maxDistanceKm} km.`,
        "error"
      );
      return;
    }
    //

    // Ask for dine mode
    if (isDeliveryOrPickupEnabled && !isDeliveryOrPickupSelected && isDeliveryOrPickupAvailable) {
      dispatch(setIsDineModeOpen(true));
      return;
    }

    // Ask for delivery location, check if delivery is selected as dinemode and deliveryLocation from redux order is empty
    if (dineMode === MENU_FLOWS.DELIVERY && (!order.deliveryLocation?.latitude || !order.deliveryLocation?.longitude)) {
      dispatch(openLocationDialog());
      return;
    }

    // Remember min value
    if (dineMode === MENU_FLOWS.DELIVERY) {
      if (deliveryMinValue > subtotal) {
        setIsMinValueDrawerOpen(true);
        return;
      }
    }

    // Navigate to checkout
    if (dineMode === MENU_FLOWS.DELIVERY || dineMode === MENU_FLOWS.PICK_UP) {
      if (organizationAlias) {
        navigate("organizations.order.checkout", { orderId });
      } else {
        navigate("checkout", { orderId });
      }
    }

    //if has branches and no active branch, open branch drawer
    if (hasBranches && !activeBranch) {
      dispatch(openBranchDrawer());
      return;
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {order.items?.length ? (
        <Stack
          px={3}
          sx={{
            boxShadow: "inset 0px -2px 4px rgba(0, 0, 0, 0.1)",
            background: "linear-gradient(to bottom, white 25%, #E4EFFF)",
            paddingY: 2,
          }}
        >
          <DineModeTabs />
          {banners?.map((banner) => React.cloneElement(banner, { key: v4() }))}
        </Stack>
      ) : null}

      {!order.items?.length ? (
        <Stack
          p={4}
          spacing={2}
          alignItems="center"
          sx={
            {
              // border: "4px solid black",
            }
          }
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Femty-orders.webp?alt=media&token=0d42dbf6-73c0-40c0-9fa6-4ea9789ee7cd"
            width={200}
            alt="Cuenta vacía"
          />
          <EmptyState
            title="Tu cuenta está vacía"
            description="Cuando agregues algo a tu cuenta aparecerá justo aquí"
            action={{
              children: "Agregar",
              onClick: onClose,
            }}
          />
        </Stack>
      ) : null}

      {order.items?.length ? (
        <Stack
          sx={{
            // border: "4px solid green",
            flex: 1,
            minHeight: 0,
            height: "auto !important",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              // border: "4px solid red",
              flex: 1,
            }}
          >
            <OrderItemsList items={order.items} variant="expanded" onClose={onClose} />
            <Stack alignItems="center">
              <Button size="small" variant="outlined" onClick={onClose} startIcon={<HugeiconsIcon icon={Add01Icon} size={18} />}>
                Agregar {t("product", 2)}
              </Button>
            </Stack>
          </div>
          <Divider color={grey[300]} />

          <Stack spacing={1} px={3} py={3}>
            {/* SUBTOTAL */}
            {showTaxes || (order?.flow && order.flow !== "dine-in") ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  <Price currency="NIO">{getOrderSubtotal(order.items)}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* DELIVERY */}
            {service.mode === "delivery" && service.isEnabled ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">
                  Envío
                  {/* add distancein km if available */}
                  {order?.deliveryLocation?.distance?.text ? ` / ${order.deliveryLocation.distance.text}` : null}
                </Typography>
                <Typography variant="body2">
                  {service.type === "fixed" ? (
                    <Price currency="NIO">{service.fee}</Price>
                  ) : !order.deliveryLocation?.latitude || !order.deliveryLocation?.longitude ? (
                    <Link onClick={() => dispatch(openLocationDialog())}>Agregar ubicación</Link>
                  ) : service.type === "auto" && !distance.isLoading && service.fee ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Link onClick={() => dispatch(openLocationDialog())}>Editar ubicación</Link>
                      <Price currency="NIO">{service.fee}</Price>
                    </Stack>
                  ) : service.type === "manual" ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Link onClick={() => dispatch(openLocationDialog())}>Editar ubicación</Link>
                      <Typography variant="body2">A convenir</Typography>
                    </Stack>
                  ) : null}
                </Typography>
              </Stack>
            ) : null}

            {/* TAXES */}
            {showTaxes ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontSize={"18px"}>
                  IVA
                </Typography>
                <Typography variant="h4" fontSize={"18px"}>
                  <Price currency="NIO">{getOrderTaxes(order.items)}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* TIPS */}
            {flowParam === "dine-in" ? (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Propina {order.tipPercentage}% (voluntaria)</Typography>
                  <Checkbox checked={order.areTipsIncluded} value={!order.areTipsIncluded} onChange={toggleTips} sx={{ padding: 0 }} />
                </Stack>
                <Typography>
                  <Price currency="NIO">{tips}</Price>
                </Typography>
              </Stack>
            ) : null}

            {/* SERVICE FEE */}
            {/* {service.fee ? <DataCell label="Envío" value={addCurrency(service.fee, "NIO")} /> : null} */}

            {/* TOTAL */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight="bold">
                Total
                <span style={{ fontWeight: "normal" }}>{service.type === "manual" ? " (envío no incluido)" : ""}</span>
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                <Price currency="NIO">{getOrderTotal(order.items as OrderItem[], showTaxes, service.fee)}</Price>
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ) : null}

      <div
        style={{
          flexShrink: 0,
          // border: "4px solid blue",
        }}
      >
        {order.items?.length && order?.flow && order.flow !== "dine-in" ? (
          <Stack px={"24px"} pb={"24px"}>
            <Button size="large" variant={banners?.length ? "outlined" : "contained"} color="primary" onClick={() => handleContinue(order.id)}>
              Continuar
            </Button>
          </Stack>
        ) : null}
      </div>

      <Drawer
        anchor="bottom"
        open={isMinValueDrawerOpen}
        onClose={() => setIsMinValueDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: isMobile ? "32px" : 0,
            borderTopRightRadius: isMobile ? "32px" : 0,
            width: isMobile ? "100%" : "50%",
          },
        }}
      >
        <Stack direction={"row"} spacing={2} alignItems="center" justifyContent="space-between" px={3} py={2}>
          <Typography variant="h3">¡Casi llegas!</Typography>
          <IconButton onClick={() => setIsMinValueDrawerOpen(false)}>
            <HugeiconsIcon icon={Cancel01Icon} />
          </IconButton>
        </Stack>
        <Stack p={4} alignItems="center" spacing={2}>
          <img
            width={225}
            height="auto"
            src="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fdelivery-driver.webp?alt=media&token=78ded6a4-fd50-41fd-9949-946c4624f88c"
            alt="Mensajero"
          />
          <Typography textAlign="center">
            Para pedidos a domicilio
            <br />
            el monto mínimo de compra es de {addCurrency(organization?.settings?.services?.delivery?.fee?.minValue || 0, "NIO")}.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(getOrderSubtotal(order.items) / (organization?.settings?.services?.delivery?.fee?.minValue || 1)) * 100}
            color="success"
            sx={{ width: "100%", height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary">
            Te faltan {addCurrency((organization?.settings?.services?.delivery?.fee?.minValue || 0) - getOrderSubtotal(order.items), "NIO")}
          </Typography>
          <Button variant="contained" size="large" fullWidth onClick={onClose}>
            Agregar {t("product", 1, true)}
          </Button>
        </Stack>
      </Drawer>
    </div>
  );
};

export default Order;
